import { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  LinearProgress,
  Snackbar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from '@mui/material'
import {
  Assessment,
  Search,
  Description,
  AutoAwesome,
  Edit,
  Delete,
  Close,
  VideoLibrary,
  Article,
  ArrowBack,
  LockOpen,
  Download,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetMemorialsQuery, useLazyDownloadMemorialQuery } from '../store/api/memorialsApi'
import { useGetDocumentsQuery, useLazyDownloadDocumentQuery, useSearchDocumentsScoringMutation } from '../store/api/documentsApi'
import {
  useGetScoresQuery,
  useCreateScoreMutation,
  useUpdateScoreMutation,
  useDeleteScoreMutation,
  useSubmitScoreMutation,
  useUnlockScoreMutation,
  useLazyGetAISuggestionsQuery,
  useGetRubricsQuery,
  useCreateRubricMutation,
  useRunAiPreliminaryMemorialMutation,
  type RubricCreate,
  type RubricCriterion,
  type Score,
} from '../store/api/scoringApi'
import { useLazyExportScoresCsvQuery } from '../store/api/reportsApi'
import { hasPermission } from '../utils/permissions'
import { formatCaseDisplay } from '../utils/caseDisplay'
import { PageHeader } from '../components/PageHeader'

export default function ScoringPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const [scoringDialogOpen, setScoringDialogOpen] = useState(false)
  const [selectedCaseId, setSelectedCaseId] = useState<string>('')
  const [selectedTeamId, setSelectedTeamId] = useState<string>('')
  const [viewMode, setViewMode] = useState<'caseList' | 'caseDetail'>('caseList')
  const [ragResults, setRagResults] = useState<any[]>([])
  const [isRAGSearching, setIsRAGSearching] = useState(false)
  const [editingScore, setEditingScore] = useState<Score | null>(null)
  const [aiSuggestions, setAISuggestions] = useState<any>(null)
  const [isLoadingAI, setIsLoadingAI] = useState(false)
  const [rubricDialogOpen, setRubricDialogOpen] = useState(false)
  const [newRubric, setNewRubric] = useState<RubricCreate>({
    name: '',
    case_id: '',
    criteria: [
      { key: 'criterion_1', name: 'Tiêu chí 1', max_score: 10, weight: 1 }
    ],
  })

  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [], isLoading: isLoadingTeams } = useGetTeamsQuery()
  const { data: memorials = [] } = useGetMemorialsQuery(
    { case_id: selectedCaseId || undefined },
    { skip: false } // Load all memorials, filter will be done in UI
  )
  // Load transcripts (documents with type TRANSCRIPT)
  const { data: transcripts = [] } = useGetDocumentsQuery(
    { 
      case_id: selectedCaseId || undefined,
      document_type: 'transcript'
    },
    { skip: false }
  )
  const { data: scores = [], refetch: refetchScores } = useGetScoresQuery(
    { case_id: selectedCaseId || undefined, team_id: selectedTeamId || undefined },
    { skip: false } // Load all scores, filter will be done in UI
  )
  // Load all rubrics (both general and case-specific)
  const { data: rubrics = [] } = useGetRubricsQuery(
    {}, // Load all rubrics, not filtered by case_id
    { skip: false }
  )

  const [createScore, { isLoading: isCreating }] = useCreateScoreMutation()
  const [updateScore, { isLoading: isUpdating }] = useUpdateScoreMutation()
  const [deleteScore] = useDeleteScoreMutation()
  const [submitScore, { isLoading: isSubmitting }] = useSubmitScoreMutation()
  const [unlockScore, { isLoading: isUnlocking }] = useUnlockScoreMutation()
  const [getAISuggestions] = useLazyGetAISuggestionsQuery()
  const [createRubric, { isLoading: isCreatingRubric }] = useCreateRubricMutation()
  const [downloadDocument] = useLazyDownloadDocumentQuery()
  const [searchDocumentsScoring] = useSearchDocumentsScoringMutation()
  const [downloadMemorial] = useLazyDownloadMemorialQuery()
  const [exportScoresCsv] = useLazyExportScoresCsvQuery()
  const [runAiPreliminary, { isLoading: prelimLoading }] = useRunAiPreliminaryMemorialMutation()
  const [aiPreliminary, setAiPreliminary] = useState<{
    governance_notice?: string
    excerpts?: Array<Record<string, unknown>>
  } | null>(null)

  const canScoreCases = currentUser && hasPermission(currentUser.role, 'SCORE_CASES')

  // State for transcript content
  const [transcriptContent, setTranscriptContent] = useState<string>('')
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false)

  // Handler for downloading transcript
  const handleDownloadTranscript = async (transcriptId: string, fileName: string) => {
    try {
      const result = await downloadDocument(transcriptId).unwrap()
      const url = window.URL.createObjectURL(result)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error: any) {
      alert(`Download transcript thất bại: ${error.data?.detail || error.message}`)
    }
  }

  // Handler for loading transcript content
  const handleLoadTranscriptContent = async (transcriptId: string) => {
    setIsLoadingTranscript(true)
    try {
      const result = await downloadDocument(transcriptId).unwrap()
      // Try to read as text
      const text = await result.text()
      setTranscriptContent(text)
    } catch (error: any) {
      console.error('Failed to load transcript content:', error)
      setTranscriptContent('')
    } finally {
      setIsLoadingTranscript(false)
    }
  }

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Scoring form state
  const [scoringForm, setScoringForm] = useState({
    case_id: '',
    team_id: '',
    memorial_id: '',
    transcript_id: '',
    submission_type: '' as 'memorial' | 'transcript' | '',
    rubric_id: '',
    rubric_scores: {} as Record<string, number>,
    comments: '',
  })

  const [ragQuery, setRagQuery] = useState('')

  // Get selected rubric
  const selectedRubric = rubrics.find((r) => r.id === scoringForm.rubric_id)
  const rubricCriteria = selectedRubric?.criteria || []

  const handleOpenScoringDialog = (
    caseId: string, 
    teamId: string, 
    submissionId?: string, 
    submissionType: 'memorial' | 'transcript' = 'memorial'
  ) => {
    // Check if score already exists
    const existingScore = scores.find((s) => {
      if (submissionType === 'memorial') {
        return s.case_id === caseId && s.team_id === teamId && s.memorial_id === submissionId
      } else {
        return s.case_id === caseId && s.team_id === teamId && s.transcript_id === submissionId
      }
    })

    if (existingScore) {
      setEditingScore(existingScore)
      setScoringForm({
        case_id: caseId,
        team_id: teamId,
        memorial_id: submissionType === 'memorial' ? (submissionId || '') : '',
        transcript_id: submissionType === 'transcript' ? (submissionId || '') : '',
        submission_type: submissionType,
        rubric_id: existingScore.rubric_id || '',
        rubric_scores: existingScore.rubric_scores || {},
        comments: existingScore.comments || '',
      })
    } else {
      setEditingScore(null)
      setScoringForm({
        case_id: caseId,
        team_id: teamId,
        memorial_id: submissionType === 'memorial' ? (submissionId || '') : '',
        transcript_id: submissionType === 'transcript' ? (submissionId || '') : '',
        submission_type: submissionType,
        // Prefer case-specific rubric, otherwise use first available (general or any)
        rubric_id: rubrics.find((r) => r.case_id === caseId)?.id || 
                   rubrics.find((r) => !r.case_id)?.id || 
                   rubrics[0]?.id || '',
        rubric_scores: {},
        comments: '',
      })
    }

    setSelectedCaseId(caseId)
    setSelectedTeamId(teamId)
    setScoringDialogOpen(true)
    setAISuggestions(null)
    setAiPreliminary(null)
  }

  const handleCloseScoringDialog = () => {
    setScoringDialogOpen(false)
    setEditingScore(null)
    setScoringForm({
      case_id: '',
      team_id: '',
      memorial_id: '',
      transcript_id: '',
      submission_type: '',
      rubric_id: '',
      rubric_scores: {},
      comments: '',
    })
    setRagQuery('')
    setRagResults([])
    setAISuggestions(null)
    setTranscriptContent('')
    setAiPreliminary(null)
  }

  // Load transcript content when transcript_id changes
  useEffect(() => {
    if (scoringForm.transcript_id && scoringForm.submission_type === 'transcript') {
      // Only load if transcript exists in DB
      const transcriptDoc = transcripts.find(t => t.id === scoringForm.transcript_id)
      if (transcriptDoc) {
        handleLoadTranscriptContent(scoringForm.transcript_id)
      } else {
        setTranscriptContent('')
      }
    } else {
      setTranscriptContent('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoringForm.transcript_id, scoringForm.submission_type])

  const handleRAGSearch = async () => {
    if (!ragQuery.trim() || !scoringForm.case_id || !scoringForm.team_id) {
      return
    }
    setIsRAGSearching(true)
    try {
      const data = await searchDocumentsScoring({
        query: ragQuery,
        case_id: scoringForm.case_id,
        team_id: scoringForm.team_id,
        limit: 10,
        similarity_threshold: 0.7,
        case_weight: 0.5,
        team_weight: 0.5,
      }).unwrap()
      setRagResults(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('RAG search failed:', error)
    } finally {
      setIsRAGSearching(false)
    }
  }

  const handleGetAISuggestions = async () => {
    const memorialId = scoringForm.memorial_id
    if (!memorialId) {
      setSnackbar({
        open: true,
        message: 'AI gợi ý chỉ khả dụng cho memorial đã được nộp. Vui lòng chọn memorial.',
        severity: 'warning',
      })
      return
    }

    setIsLoadingAI(true)
    try {
      // Real API call — backend dùng RAG + rubric thực tế (không mock)
      const result = await getAISuggestions(memorialId).unwrap()
      setAISuggestions(result)

      // KHÔNG auto-fill scores. Để judge bấm "Áp dụng" cho từng tiêu chí.
      // (Áp dụng tự động làm judge dễ bỏ qua bước review.)

      const conf = result.confidence ?? 0
      const sevText =
        conf >= 0.7
          ? 'AI đã phân tích — confidence cao, hãy review trước khi áp dụng'
          : conf >= 0.4
          ? 'AI đã phân tích — confidence trung bình, cần review kỹ'
          : 'AI có ít dữ liệu để gợi ý — vui lòng chấm thủ công'
      setSnackbar({
        open: true,
        message: sevText,
        severity: conf >= 0.7 ? 'success' : conf >= 0.4 ? 'info' : 'warning',
      })
    } catch (error: any) {
      const detail = error?.data?.detail || error?.message || 'Không xác định'
      setAISuggestions(null)
      setSnackbar({
        open: true,
        message: `Không thể lấy AI gợi ý: ${detail}. Vui lòng kiểm tra Ollama server hoặc chấm thủ công.`,
        severity: 'error',
      })
    } finally {
      setIsLoadingAI(false)
    }
  }

  // Áp dụng gợi ý cho 1 tiêu chí
  const applyAISuggestionForCriterion = (criterionKey: string) => {
    if (!aiSuggestions?.suggested_scores) return
    const val = aiSuggestions.suggested_scores[criterionKey]
    if (val === undefined) return
    setScoringForm({
      ...scoringForm,
      rubric_scores: { ...scoringForm.rubric_scores, [criterionKey]: val },
    })
    setSnackbar({
      open: true,
      message: `Đã áp dụng gợi ý AI cho tiêu chí (${val})`,
      severity: 'success',
    })
  }

  // Áp dụng tất cả gợi ý
  const applyAllAISuggestions = () => {
    if (!aiSuggestions?.suggested_scores || !selectedRubric?.criteria) return
    const newScores: Record<string, number> = { ...scoringForm.rubric_scores }
    selectedRubric.criteria.forEach((c) => {
      const v = aiSuggestions.suggested_scores[c.key]
      if (v !== undefined) newScores[c.key] = v
    })
    setScoringForm({ ...scoringForm, rubric_scores: newScores })
    setSnackbar({
      open: true,
      message: 'Đã áp dụng tất cả gợi ý AI vào phiếu điểm',
      severity: 'success',
    })
  }

  const calculateTotalScore = () => {
    if (!selectedRubric || !selectedRubric.criteria.length) {
      // Simple sum if no rubric, then normalize to 10
      const scores = Object.values(scoringForm.rubric_scores)
      const sum = scores.reduce((sum, score) => sum + score, 0)
      const maxSum = selectedRubric?.criteria.reduce((sum, c) => sum + c.max_score, 0) || 10
      return maxSum > 0 ? (sum / maxSum) * 10 : 0
    }

    // Calculate weighted average, then normalize to scale of 10
    let totalWeightedScore = 0
    let totalWeightedMax = 0

    selectedRubric.criteria.forEach((criterion) => {
      const score = scoringForm.rubric_scores[criterion.key] || 0
      const weight = criterion.weight || 1.0
      totalWeightedScore += score * weight
      totalWeightedMax += criterion.max_score * weight
    })

    if (totalWeightedMax === 0) return 0
    
    // Normalize to scale of 10
    return (totalWeightedScore / totalWeightedMax) * 10
  }

  const getMaxScore = () => {
    // Always return 10 as max score
    return 10
  }

  const scoreFormLocked = editingScore?.status === 'submitted'

  const handleSubmitScore = async () => {
    if (scoreFormLocked) {
      setSnackbar({
        open: true,
        message: 'Phiếu đã gửi chính thức. BTC có thể mở khóa để chỉnh sửa.',
        severity: 'warning',
      })
      return
    }
    if (Object.keys(scoringForm.rubric_scores).length === 0) {
      setSnackbar({
        open: true,
        message: 'Vui lòng chấm điểm cho ít nhất một tiêu chí',
        severity: 'warning',
      })
      return
    }

    try {
      if (editingScore) {
        // Update existing score
        await updateScore({
          id: editingScore.id,
          update: {
            rubric_scores: scoringForm.rubric_scores,
            comments: scoringForm.comments,
            ai_suggestions: aiSuggestions,
          },
        }).unwrap()
        setSnackbar({
          open: true,
          message: 'Cập nhật điểm thành công!',
          severity: 'success',
        })
      } else {
        // Create new score
        await createScore({
          case_id: scoringForm.case_id,
          team_id: scoringForm.team_id,
          memorial_id: scoringForm.memorial_id || undefined,
          transcript_id: scoringForm.transcript_id || undefined,
          submission_type: scoringForm.submission_type || undefined,
          rubric_id: scoringForm.rubric_id || undefined,
          rubric_scores: scoringForm.rubric_scores,
          comments: scoringForm.comments,
        }).unwrap()
        setSnackbar({
          open: true,
          message: 'Chấm điểm thành công!',
          severity: 'success',
        })
      }
      refetchScores()
      handleCloseScoringDialog()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Chấm điểm thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }

  const handleFinalizeScore = async () => {
    if (scoreFormLocked) {
      setSnackbar({
        open: true,
        message: 'Phiếu đã được gửi trước đó.',
        severity: 'info',
      })
      return
    }
    if (Object.keys(scoringForm.rubric_scores).length === 0) {
      setSnackbar({
        open: true,
        message: 'Vui lòng chấm điểm cho ít nhất một tiêu chí',
        severity: 'warning',
      })
      return
    }
    try {
      let scoreId = editingScore?.id
      if (editingScore) {
        const updated = await updateScore({
          id: editingScore.id,
          update: {
            rubric_scores: scoringForm.rubric_scores,
            comments: scoringForm.comments,
            ai_suggestions: aiSuggestions,
          },
        }).unwrap()
        scoreId = updated.id
      } else {
        const created = await createScore({
          case_id: scoringForm.case_id,
          team_id: scoringForm.team_id,
          memorial_id: scoringForm.memorial_id || undefined,
          transcript_id: scoringForm.transcript_id || undefined,
          submission_type: scoringForm.submission_type || undefined,
          rubric_id: scoringForm.rubric_id || undefined,
          rubric_scores: scoringForm.rubric_scores,
          comments: scoringForm.comments,
          ai_suggestions: aiSuggestions || undefined,
        }).unwrap()
        scoreId = created.id
      }
      if (scoreId) {
        await submitScore(scoreId).unwrap()
      }
      setSnackbar({
        open: true,
        message: 'Đã gửi điểm chính thức. Phiếu đã khóa.',
        severity: 'success',
      })
      refetchScores()
      handleCloseScoringDialog()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Gửi điểm thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }

  const handleUnlockScoreRow = async (scoreId: string) => {
    try {
      await unlockScore(scoreId).unwrap()
      setSnackbar({
        open: true,
        message: 'Đã mở khóa phiếu điểm.',
        severity: 'success',
      })
      refetchScores()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Mở khóa thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }

  const handleDeleteScore = async (scoreId: string) => {
    if (!confirm('Bạn có chắc muốn xóa điểm này?')) return

    try {
      await deleteScore(scoreId).unwrap()
      setSnackbar({
        open: true,
        message: 'Xóa điểm thành công!',
        severity: 'success',
      })
      refetchScores()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Xóa điểm thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }

  if (!canScoreCases) {
    return (
      <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
        <Alert severity="error">
          Bạn không có quyền chấm điểm. Chỉ Judge và Secretary mới có quyền này.
        </Alert>
      </Container>
    )
  }

  // Get cases that have memorials or transcripts submitted
  const casesWithSubmissions = cases.filter((c) => {
    const caseMemorials = memorials.filter((m) => m.case_id === c.id)
    const caseTranscripts = transcripts.filter((t) => t.case_id === c.id)
    return caseMemorials.length > 0 || caseTranscripts.length > 0
  })

  // Calculate stats for each case
  const getCaseStats = (caseId: string) => {
    const caseMemorials = memorials.filter((m) => m.case_id === caseId)
    const caseTranscripts = transcripts.filter((t) => t.case_id === caseId)
    const caseScores = scores.filter((s) => s.case_id === caseId)
    
    const memorialsScored = caseMemorials.filter((m) => 
      caseScores.some((s) => s.memorial_id === m.id || (s.case_id === caseId && s.team_id === m.team_id && s.submission_type === 'memorial'))
    ).length
    
    // Count transcript scores (including scores for teams without transcript in DB)
    const transcriptsScored = caseScores.filter((s) => 
      s.case_id === caseId && s.submission_type === 'transcript'
    ).length
    
    // Total transcripts = actual transcripts in DB OR number of teams with memorials
    const uniqueTeamIds = [...new Set(caseMemorials.map(m => m.team_id))]
    const totalTranscripts = Math.max(caseTranscripts.length, uniqueTeamIds.length)
    
    return {
      totalMemorials: caseMemorials.length,
      totalTranscripts: totalTranscripts,
      scoredMemorials: memorialsScored,
      scoredTranscripts: transcriptsScored,
      pendingMemorials: caseMemorials.length - memorialsScored,
      pendingTranscripts: totalTranscripts - transcriptsScored,
    }
  }

  // Handle case selection
  const handleSelectCase = (caseId: string) => {
    setSelectedCaseId(caseId)
    setSelectedTeamId('')
    setViewMode('caseDetail')
  }

  // Handle back to case list
  const handleBackToCaseList = () => {
    setViewMode('caseList')
    setSelectedCaseId('')
    setSelectedTeamId('')
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title="Hệ thống Chấm điểm"
        subtitle="Chấm memorial, bản ghi tranh tụng và quản lý rubric theo từng cuộc thi."
        icon={<Assessment />}
        actions={
          canScoreCases ? (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<Download />}
              onClick={async () => {
                try {
                  const blob = await exportScoresCsv().unwrap()
                  const url = window.URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = 'scores_export.csv'
                  document.body.appendChild(a)
                  a.click()
                  window.URL.revokeObjectURL(url)
                  document.body.removeChild(a)
                } catch (e: any) {
                  alert(e?.data?.detail || e?.message || 'Xuất CSV thất bại')
                }
              }}
            >
              Xuất CSV phiếu điểm (phạm vi quyền)
            </Button>
          ) : null
        }
      />

      {viewMode === 'caseList' ? (
        /* View 1: Danh sách cuộc thi - Dạng bảng */
        <Paper sx={{ mt: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Danh sách cuộc thi cần chấm điểm
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click vào một cuộc thi để xem chi tiết các submissions cần chấm điểm
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Cuộc thi</strong></TableCell>
                  <TableCell align="center"><strong>Memorial</strong></TableCell>
                  <TableCell align="center"><strong>Video tranh tụng</strong></TableCell>
                  <TableCell align="center"><strong>Tổng cần chấm</strong></TableCell>
                  <TableCell align="right"><strong>Thao tác</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {casesWithSubmissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Alert severity="info" sx={{ my: 2 }}>
                        Chưa có cuộc thi nào cần chấm điểm
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  casesWithSubmissions.map((caseItem: any) => {
                    const stats = getCaseStats(caseItem.id)
                    const totalPending = stats.pendingMemorials + stats.pendingTranscripts
                    
                    return (
                      <TableRow
                        key={caseItem.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            cursor: 'pointer'
                          }
                        }}
                        onClick={() => handleSelectCase(caseItem.id)}
                      >
                        <TableCell>
                          <Typography variant="body1" fontWeight={500}>
                            {formatCaseDisplay(caseItem)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {stats.scoredMemorials}/{stats.totalMemorials}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {stats.scoredTranscripts}/{stats.totalTranscripts}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600} color={totalPending > 0 ? 'error.main' : 'success.main'}>
                            {totalPending}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleSelectCase(caseItem.id)
                            }}
                          >
                            Xem chi tiết
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        /* View 2: Chi tiết submissions của cuộc thi đã chọn */
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={handleBackToCaseList} color="primary">
              <ArrowBack />
            </IconButton>
            <Typography variant="h5">
              {cases.find((c) => c.id === selectedCaseId) ? formatCaseDisplay(cases.find((c) => c.id === selectedCaseId)!) : 'Chi tiết chấm điểm'}
            </Typography>
          </Box>

          {/* Filter by Team */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Lọc theo đội thi</InputLabel>
              <Select
                value={selectedTeamId}
                label="Lọc theo đội thi"
                onChange={(e) => setSelectedTeamId(e.target.value)}
              >
                <MenuItem value="">Tất cả đội thi</MenuItem>
                {(() => {
                  const caseMemorials = memorials.filter((m) => m.case_id === selectedCaseId)
                  const caseTranscripts = transcripts.filter((t) => t.case_id === selectedCaseId)
                  const allTeamIds = [...new Set([
                    ...caseMemorials.map((m) => m.team_id),
                    ...caseTranscripts.map((t) => t.team_id),
                  ])]
                  
                  return allTeamIds.map((teamId) => {
                    const team = teams.find((t) => t.id === teamId)
                    return team ? (
                      <MenuItem key={teamId} value={teamId}>
                        {team.name} - {team.university}
                      </MenuItem>
                    ) : null
                  })
                })()}
              </Select>
            </FormControl>
          </Paper>

          {/* Submissions Table (Memorials + Transcripts) */}
          <Paper>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Danh sách cần chấm điểm
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Bao gồm: Memorial (vòng chấm viết) và Video tranh tụng online (vòng tranh tụng)
              </Typography>
            </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Loại</strong></TableCell>
                <TableCell><strong>Cuộc thi</strong></TableCell>
                <TableCell><strong>Đội thi</strong></TableCell>
                <TableCell><strong>File</strong></TableCell>
                <TableCell><strong>Kích thước</strong></TableCell>
                <TableCell><strong>Điểm</strong></TableCell>
                <TableCell><strong>Ngày chấm</strong></TableCell>
                <TableCell align="right"><strong>Thao tác</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingTeams ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (() => {
                // Combine memorials and transcripts
                let allSubmissions: any[] = [
                  ...memorials.map(m => ({ ...m, type: 'memorial' as const })),
                  ...transcripts.map(t => ({ ...t, type: 'transcript' as const }))
                ].filter((item) => {
                  // Always filter by selectedCaseId in caseDetail view
                  if (item.case_id !== selectedCaseId) return false
                  if (selectedTeamId && item.team_id !== selectedTeamId) return false
                  return true
                })

                // If no transcripts in DB, create dummy entries for teams that have memorials (for POC demo)
                if (transcripts.filter(t => t.case_id === selectedCaseId).length === 0) {
                  const caseMemorials = memorials.filter(m => m.case_id === selectedCaseId)
                  const uniqueTeamIds = [...new Set(caseMemorials.map(m => m.team_id))]
                  
                  const dummyTranscripts = uniqueTeamIds
                    .filter(teamId => {
                      if (selectedTeamId && teamId !== selectedTeamId) return false
                      // Only add if team doesn't already have a transcript score
                      const hasTranscriptScore = scores.some(s => 
                        s.case_id === selectedCaseId && 
                        s.team_id === teamId && 
                        s.submission_type === 'transcript'
                      )
                      return !hasTranscriptScore
                    })
                    .map(teamId => ({
                      id: `dummy-transcript-${teamId}`,
                      case_id: selectedCaseId,
                      team_id: teamId,
                      type: 'transcript' as const,
                      title: 'Video tranh tụng online',
                      file_name: 'video-transcript.txt',
                      file_size: 0,
                      document_type: 'transcript' as any,
                      uploaded_at: new Date().toISOString(),
                      uploaded_by: '',
                      storage_path: '',
                      mime_type: 'text/plain',
                      status: 'active',
                      chunk_count: 0,
                    }))
                  
                  allSubmissions = [...allSubmissions, ...dummyTranscripts]
                }

                if (allSubmissions.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Alert severity="info" sx={{ my: 2 }}>
                          Chưa có bài nộp nào cần chấm điểm.
                        </Alert>
                      </TableCell>
                    </TableRow>
                  )
                }

                return allSubmissions.map((item) => {
                  const caseItem = cases.find((c) => c.id === item.case_id)
                  const team = teams.find((t) => t.id === item.team_id)
                  const teamName = team ? team.name : item.team_id
                  
                  const existingScore = scores.find((s) => {
                    if (item.type === 'memorial') {
                      return s.memorial_id === item.id || 
                             (s.case_id === item.case_id && s.team_id === item.team_id && s.submission_type === 'memorial')
                    } else {
                      return s.transcript_id === item.id || 
                             (s.case_id === item.case_id && s.team_id === item.team_id && s.submission_type === 'transcript')
                    }
                  })

                  return (
                    <TableRow
                      key={`${item.type}-${item.id}`}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: 'pointer'
                        }
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={item.type === 'memorial' ? 'Memorial' : 'Video tranh tụng'}
                          color={item.type === 'memorial' ? 'primary' : 'secondary'}
                          size="small"
                          icon={item.type === 'memorial' ? <Description /> : <VideoLibrary />}
                          sx={{ 
                            fontWeight: item.type === 'transcript' ? 600 : 'normal',
                            border: item.type === 'transcript' ? 2 : 0,
                            borderColor: item.type === 'transcript' ? 'secondary.main' : 'transparent'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {caseItem ? formatCaseDisplay(caseItem) : item.case_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {teamName}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {item.type === 'memorial' ? item.file_name : (item.title || item.file_name || 'Video tranh tụng')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.file_size > 0 ? `${(item.file_size / 1024 / 1024).toFixed(2)} MB` : '-'}
                      </TableCell>
                      <TableCell>
                        {existingScore ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Chip
                              label={existingScore.total_score.toFixed(2)}
                              color="success"
                              size="small"
                            />
                            {existingScore.status === 'submitted' && (
                              <Chip label="Đã gửi" color="warning" size="small" variant="outlined" />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Chưa chấm
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {existingScore?.scored_at ? (
                          <Typography variant="body2">
                            {new Date(existingScore.scored_at).toLocaleString('vi-VN')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant={existingScore ? 'outlined' : 'contained'}
                            color={item.type === 'transcript' ? 'secondary' : 'primary'}
                            startIcon={existingScore ? <Edit /> : <Assessment />}
                            onClick={(e) => {
                              e.stopPropagation()
                              // For transcripts without DB entry, use empty string as ID
                              const submissionId = item.id.startsWith('dummy-transcript-') ? '' : item.id
                              handleOpenScoringDialog(
                                item.case_id || '', 
                                item.team_id || '', 
                                submissionId, 
                                item.type
                              )
                            }}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 500,
                              borderRadius: 1,
                            }}
                          >
                            {existingScore
                              ? existingScore.status === 'submitted'
                                ? 'Xem phiếu'
                                : 'Sửa điểm'
                              : 'Chấm điểm'}
                          </Button>
                          {existingScore &&
                            existingScore.status === 'submitted' &&
                            currentUser &&
                            hasPermission(currentUser.role, 'MANAGE_USERS') && (
                              <IconButton
                                size="small"
                                color="primary"
                                title="Mở khóa để chỉnh sửa"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleUnlockScoreRow(existingScore.id)
                                }}
                                disabled={isUnlocking}
                              >
                                <LockOpen fontSize="small" />
                              </IconButton>
                            )}
                          {existingScore &&
                            (existingScore.status !== 'submitted' ||
                              (currentUser &&
                                hasPermission(currentUser.role, 'MANAGE_USERS'))) && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteScore(existingScore.id)
                              }}
                              title="Xóa điểm"
                              sx={{
                                border: '1px solid',
                                borderColor: 'error.main',
                                borderRadius: 1,
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                })
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
        </Box>
      )}

      {/* Scoring Dialog */}
      <Dialog 
        open={scoringDialogOpen} 
        onClose={handleCloseScoringDialog} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: { xs: '100vh', sm: '95vh', md: '95vh' },
            height: { xs: '100vh', sm: '95vh', md: '95vh' },
            m: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 1 },
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Assessment />
            <Typography variant="h6">Chấm điểm</Typography>
            {scoringForm.submission_type === 'transcript' && (
              <Chip label="Video tranh tụng" color="secondary" size="small" sx={{ ml: 1 }} />
            )}
            {scoreFormLocked && (
              <Chip label="Đã gửi (khóa)" color="warning" size="small" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 0 }}>
          {scoringForm.submission_type === 'transcript' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              {/* Hàng trên: Cuộc thi, Đội thi, Danh sách tham dự - Tỷ lệ 3/3/6 */}
              <Paper sx={{ 
                p: { xs: 1, sm: 1.5 }, 
                backgroundColor: 'grey.50', 
                mx: { xs: 1, sm: 2 }, 
                mt: { xs: 1, sm: 2 }, 
                mb: { xs: 1, sm: 2 } 
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'flex-start', md: 'flex-start' }, 
                  gap: { xs: 1.5, md: 2 } 
                }}>
                  <Box sx={{ 
                    flex: { xs: '1 1 100%', md: '0 0 25%' }, 
                    maxWidth: { xs: '100%', md: '25%' },
                    width: { xs: '100%', md: 'auto' }
                  }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Cuộc thi
                    </Typography>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {cases.find((c) => c.id === scoringForm.case_id)?.title || scoringForm.case_id}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                  <Divider sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }} />
                  <Box sx={{ 
                    flex: { xs: '1 1 100%', md: '0 0 25%' }, 
                    maxWidth: { xs: '100%', md: '25%' },
                    width: { xs: '100%', md: 'auto' }
                  }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Đội thi
                    </Typography>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {(() => {
                        const team = teams.find((t) => t.id === scoringForm.team_id)
                        return team ? team.name : (isLoadingTeams ? 'Đang tải...' : scoringForm.team_id)
                      })()}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                  <Divider sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }} />
                  <Box sx={{ 
                    flex: { xs: '1 1 100%', md: '0 0 50%' }, 
                    maxWidth: { xs: '100%', md: '50%' },
                    width: { xs: '100%', md: 'auto' }
                  }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Danh sách tham dự
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {(() => {
                        const currentTeam = teams.find(t => t.id === scoringForm.team_id)
                        const otherTeams = teams.filter(t => t.id !== scoringForm.team_id)
                        
                        const participants = [
                          { role: 'Thẩm phán', name: 'Nguyễn Văn A' },
                          { role: 'Nguyên đơn', name: currentTeam ? currentTeam.name : 'N/A' },
                          { role: 'Bị đơn', name: otherTeams[0] ? otherTeams[0].name : 'Đối thủ' },
                          { role: 'Thư ký', name: 'Trần Thị B' },
                          { role: 'Quan sát viên', name: 'Lê Văn C' }
                        ]
                        
                        return participants.map((p, idx) => (
                          <Chip 
                            key={idx}
                            label={`${p.role}: ${p.name}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        ))
                      })()}
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* Bên dưới: Chia 2 cột trái/phải */}
              <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ 
                flex: 1, 
                overflow: 'hidden', 
                m: 0, 
                px: { xs: 1, sm: 2 }, 
                pb: { xs: 1, sm: 2 } 
              }}>
                {/* Cột trái: Video + Transcript */}
                <Grid item xs={12} md={6} sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  overflow: 'hidden', 
                  height: { 
                    xs: 'auto', 
                    sm: 'calc(95vh - 250px)', 
                    md: 'calc(95vh - 200px)' 
                  },
                  minHeight: { xs: '400px', sm: 'auto' }
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'auto' }}>

                  {/* Video Player */}
                  <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      <VideoLibrary fontSize="small" />
                      Video Tranh Tụng
                    </Typography>
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56.25%', // 16:9 aspect ratio
                        backgroundColor: 'grey.900',
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      {(() => {
                        const transcriptDoc = transcripts.find(t => t.id === scoringForm.transcript_id)
                        
                        // Ưu tiên lấy video URL từ courtroom recording, nếu không có thì dùng YouTube video mới
                        const videoUrl = transcriptDoc?.description
                        
                        // Nếu có video URL từ courtroom recording (không phải YouTube)
                        if (videoUrl && !videoUrl.includes('youtube.com') && !videoUrl.includes('youtu.be')) {
                          return (
                            <video
                              controls
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                              }}
                              src={videoUrl}
                            >
                              Trình duyệt của bạn không hỗ trợ video tag.
                            </video>
                          )
                        }
                        
                        // YouTube embed (dummy video mới hoặc từ transcript description)
                        const youtubeUrl = videoUrl || 'https://www.youtube.com/watch?v=cwtZSdYE7bo'
                        // Extract video ID from YouTube URL
                        const getYouTubeVideoId = (url: string) => {
                          const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
                          const match = url.match(regExp)
                          return (match && match[2].length === 11) ? match[2] : null
                        }
                        
                        const videoId = getYouTubeVideoId(youtubeUrl) || 'cwtZSdYE7bo'
                        
                        return (
                          <iframe
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              border: 'none',
                            }}
                            src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title="Video tranh tụng"
                          />
                        )
                      })()}
                    </Box>
                  </Paper>

                  {/* Transcript Viewer */}
                  <Paper sx={{ p: { xs: 1.5, sm: 2 }, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: { xs: '300px', sm: 0 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 1.5 } }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        <Article fontSize="small" />
                        Transcript
                      </Typography>
                      {(() => {
                        const transcriptDoc = transcripts.find(t => t.id === scoringForm.transcript_id)
                        
                        return transcriptDoc ? (
                          <Button
                            size="small"
                            startIcon={<Description />}
                            onClick={() => handleDownloadTranscript(transcriptDoc.id, transcriptDoc.file_name)}
                          >
                            Tải Transcript
                          </Button>
                        ) : null
                      })()}
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        overflowY: 'auto',
                        p: 2,
                        backgroundColor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {(() => {
                        const transcriptDoc = transcripts.find(t => t.id === scoringForm.transcript_id)
                        
                        // If transcript not found in DB, show transcript content
                        if (!transcriptDoc) {
                          const defaultTranscriptContent = `TRANSCRIPT - VÒNG TRANH TỤNG ONLINE

Thời gian: 10:00 - 12:00, Ngày ${new Date().toLocaleDateString('vi-VN')}
Cuộc thi: ${cases.find(c => c.id === scoringForm.case_id)?.title || 'N/A'}
Đội thi: ${teams.find(t => t.id === scoringForm.team_id)?.name || 'N/A'}

=== PHẦN MỞ ĐẦU ===

[10:00:15] Thẩm phán: Phiên tòa xét xử vụ án được mở. Mời các bên trình bày.

[10:01:30] Nguyên đơn: Kính thưa Hội đồng xét xử, chúng tôi xin trình bày yêu cầu của mình...

[10:05:45] Bị đơn: Kính thưa Hội đồng, chúng tôi phản đối yêu cầu của nguyên đơn vì...

=== PHẦN TRANH TỤNG ===

[10:15:20] Thẩm phán: Mời nguyên đơn trình bày chứng cứ.

[10:16:10] Nguyên đơn: Chúng tôi xin đưa ra các chứng cứ sau:
1. Hợp đồng ký ngày 15/01/2024
2. Biên bản giao nhận hàng hóa
3. Email trao đổi giữa hai bên

[10:25:30] Bị đơn: Chúng tôi phản đối chứng cứ số 3 vì email đó không có chữ ký điện tử...

[10:30:15] Thẩm phán: Mời nguyên đơn phản hồi.

[10:31:00] Nguyên đơn: Email đó được gửi từ địa chỉ chính thức của công ty bị đơn...

=== PHẦN KẾT LUẬN ===

[11:45:00] Thẩm phán: Các bên có còn ý kiến gì nữa không?

[11:45:30] Nguyên đơn: Không, thưa Hội đồng.

[11:45:35] Bị đơn: Không, thưa Hội đồng.

[11:46:00] Thẩm phán: Phiên tòa tạm nghỉ. Hội đồng sẽ xem xét và đưa ra quyết định.

=== KẾT THÚC ===

[12:00:00] Phiên tòa kết thúc.`

                          return (
                            <Box>
                              <Typography variant="body2" fontWeight={600} gutterBottom>
                                Transcript - Vòng Tranh Tụng Online
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap',
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  lineHeight: 1.6,
                                }}
                              >
                                {defaultTranscriptContent}
                              </Typography>
                            </Box>
                          )
                        }
                        
                        // Hiển thị transcript content từ DB
                        return (
                          <Box>
                            <Typography variant="body2" fontWeight={600} gutterBottom>
                              {transcriptDoc.title}
                            </Typography>
                            {isLoadingTranscript ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : transcriptContent ? (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap',
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  lineHeight: 1.6,
                                }}
                              >
                                {transcriptContent}
                              </Typography>
                            ) : transcriptDoc.description ? (
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {transcriptDoc.description}
                              </Typography>
                            ) : (
                              <Alert severity="info">
                                <Typography variant="body2">
                                  Transcript đã được upload. Nội dung đang được tải...
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                  File: {transcriptDoc.file_name} ({(transcriptDoc.file_size / 1024 / 1024).toFixed(2)} MB)
                                </Typography>
                              </Alert>
                            )}
                          </Box>
                        )
                      })()}
                    </Box>
                  </Paper>
                </Box>
              </Grid>

                {/* Cột phải: Form chấm điểm + AI Assistive */}
                <Grid item xs={12} md={6} sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  overflow: 'hidden', 
                  height: { 
                    xs: 'auto', 
                    sm: 'calc(95vh - 250px)', 
                    md: 'calc(95vh - 200px)' 
                  },
                  maxHeight: { 
                    xs: 'none', 
                    sm: 'calc(95vh - 250px)', 
                    md: 'calc(95vh - 200px)' 
                  },
                  minHeight: { xs: '400px', sm: 'auto' }
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: { xs: 1.5, sm: 2 }, 
                    overflow: 'auto', 
                    pr: { xs: 0, sm: 1 }, 
                    height: '100%', 
                    minHeight: 0, 
                    pb: { xs: 1, sm: 2 }, 
                    m: { xs: 0, sm: '5px' } 
                  }}>
                  {/* Rubric Selection */}
                  {rubrics.length > 0 && (
                    <FormControl sx={{ mt: '5px' }} fullWidth>
                      <InputLabel id="rubric-select-label">Rubric chấm điểm</InputLabel>
                      <Select
                        value={scoringForm.rubric_id}
                        label="Rubric chấm điểm"
                        labelId="rubric-select-label"
                        onChange={(e) => {
                          const rubricId = e.target.value
                          setScoringForm({
                            ...scoringForm,
                            rubric_id: rubricId,
                            // Reset scores when changing rubric
                            rubric_scores: {},
                          })
                        }}
                      >
                        {rubrics
                          .filter((r) => !r.case_id || r.case_id === scoringForm.case_id)
                          .map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                              {r.name}
                              {r.case_id && r.case_id === scoringForm.case_id && (
                                <Chip label="Cuộc thi này" size="small" sx={{ ml: 1 }} />
                              )}
                              {!r.case_id && (
                                <Chip label="Chung" size="small" color="primary" sx={{ ml: 1 }} />
                              )}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* AI Assistive Button */}
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<AutoAwesome />}
                      onClick={handleGetAISuggestions}
                      disabled={isLoadingAI || scoreFormLocked}
                      fullWidth
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        py: 1.5,
                      }}
                    >
                      {isLoadingAI ? <CircularProgress size={20} /> : 'AI Assistive'}
                    </Button>
                    {aiSuggestions && (
                      <Alert
                        severity={
                          (aiSuggestions.confidence ?? 0) >= 0.7
                            ? 'success'
                            : (aiSuggestions.confidence ?? 0) >= 0.4
                            ? 'info'
                            : 'warning'
                        }
                        sx={{ mt: 2 }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            🤖 Phân tích AI sơ bộ
                          </Typography>
                          {aiSuggestions.suggested_scores &&
                            Object.keys(aiSuggestions.suggested_scores).length > 0 && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={applyAllAISuggestions}
                                disabled={scoreFormLocked}
                                sx={{ textTransform: 'none' }}
                              >
                                Áp dụng tất cả
                              </Button>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                          {aiSuggestions.analysis}
                        </Typography>
                        {aiSuggestions.recommendations && aiSuggestions.recommendations.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                              Đề xuất:
                            </Typography>
                            <Box component="ul" sx={{ margin: 0, paddingLeft: 3, mt: 1 }}>
                              {aiSuggestions.recommendations.map((rec: string, idx: number) => (
                                <li key={idx}>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>{rec}</Typography>
                                </li>
                              ))}
                            </Box>
                          </Box>
                        )}
                        {aiSuggestions.confidence != null && (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Độ tin cậy:
                            </Typography>
                            <Box
                              sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 1,
                                background: '#E0E0E0',
                                overflow: 'hidden',
                                maxWidth: 180,
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${(aiSuggestions.confidence * 100).toFixed(0)}%`,
                                  height: '100%',
                                  background:
                                    aiSuggestions.confidence >= 0.7
                                      ? '#2E7D32'
                                      : aiSuggestions.confidence >= 0.4
                                      ? '#1565C0'
                                      : '#EF6C00',
                                }}
                              />
                            </Box>
                            <Typography variant="caption" fontWeight={600}>
                              {(aiSuggestions.confidence * 100).toFixed(0)}%
                            </Typography>
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                          AI chỉ gợi ý — Giám khảo có toàn quyền quyết định điểm cuối (P5-AI-2).
                        </Typography>
                      </Alert>
                    )}
                  </Box>

                  <Divider />

                  {/* Rubric Scores */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Điểm theo Rubric
                    </Typography>
                    {rubricCriteria.length > 0 ? (
                      rubricCriteria.map((criterion) => {
                        const aiVal = aiSuggestions?.suggested_scores?.[criterion.key]
                        const judgeVal = scoringForm.rubric_scores[criterion.key]
                        const diverged =
                          aiVal !== undefined &&
                          judgeVal !== undefined &&
                          Math.abs(judgeVal - aiVal) >= 1.0
                        return (
                        <Box key={criterion.key} sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={500}>
                              {criterion.name}
                            </Typography>
                            {criterion.description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {criterion.description}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                              {criterion.weight && criterion.weight !== 1.0 && (
                                <Chip label={`Weight: ${criterion.weight}`} size="small" />
                              )}
                              {aiVal !== undefined && (
                                <Chip
                                  label={`🤖 AI: ${aiVal.toFixed(1)}`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  onClick={
                                    scoreFormLocked
                                      ? undefined
                                      : () => applyAISuggestionForCriterion(criterion.key)
                                  }
                                  sx={{ cursor: scoreFormLocked ? 'default' : 'pointer' }}
                                />
                              )}
                              {diverged && (
                                <Chip
                                  label={`Lệch ${(judgeVal - aiVal).toFixed(1)}`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                            <TextField
                              type="number"
                              value={scoringForm.rubric_scores[criterion.key] || ''}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0
                                const clampedValue = Math.max(0, Math.min(value, criterion.max_score))
                                setScoringForm({
                                  ...scoringForm,
                                  rubric_scores: {
                                    ...scoringForm.rubric_scores,
                                    [criterion.key]: clampedValue,
                                  },
                                })
                              }}
                              disabled={scoreFormLocked}
                              inputProps={{
                                min: 0,
                                max: criterion.max_score,
                                step: 0.1,
                                style: { textAlign: 'right', padding: '8px' }
                              }}
                              size="small"
                              sx={{
                                width: 80,
                                '& .MuiOutlinedInput-input': {
                                  textAlign: 'right'
                                }
                              }}
                              placeholder="0"
                            />
                            <Typography variant="body1" color="text.secondary">
                              / {criterion.max_score}
                            </Typography>
                          </Box>
                        </Box>
                      )})
                    ) : (
                      <Alert severity="warning">
                        Chưa có rubric nào. Vui lòng chọn rubric hoặc tạo rubric mới.
                      </Alert>
                    )}
                  </Box>

                  <Divider />

                  {/* Total Score */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Tổng điểm: {calculateTotalScore().toFixed(2)} / {getMaxScore().toFixed(1)}
                    </Typography>
                    {editingScore && (
                      <Typography variant="caption" color="text.secondary">
                        Điểm cũ: {editingScore.total_score.toFixed(2)} / 10
                      </Typography>
                    )}
                  </Box>

                  {/* Comments */}
                  <TextField
                    fullWidth
                    label="Nhận xét"
                    value={scoringForm.comments}
                    onChange={(e) => setScoringForm({ ...scoringForm, comments: e.target.value })}
                    multiline
                    rows={4}
                    disabled={scoreFormLocked}
                    placeholder="Nhập nhận xét về video tranh tụng..."
                  />
                </Box>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Grid
              container
              spacing={2}
              sx={{ p: { xs: 1, sm: 2 }, maxHeight: 'calc(95vh - 120px)', overflow: 'hidden' }}
            >
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  overflow: 'auto',
                  maxHeight: { xs: '50vh', md: 'calc(95vh - 140px)' },
                  pr: { md: 1 },
                }}
              >
              <Paper sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Cuộc thi
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {cases.find((c) => c.id === scoringForm.case_id)?.title || scoringForm.case_id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Đội thi
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {(() => {
                        const team = teams.find((t) => t.id === scoringForm.team_id)
                        return team ? team.name : (isLoadingTeams ? 'Đang tải...' : scoringForm.team_id)
                      })()}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
              {(() => {
                const mem = memorials.find((m) => m.id === scoringForm.memorial_id)
                if (!mem) {
                  return (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Không tìm thấy memorial trong danh sách đã tải. Vẫn có thể xem phiếu điểm nếu đã tồn tại.
                    </Alert>
                  )
                }
                return (
                  <Paper sx={{ p: 2, mt: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Hồ sơ memorial
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {mem.file_name}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Description />}
                      onClick={async () => {
                        try {
                          const blob = await downloadMemorial(mem.id).unwrap()
                          const url = window.URL.createObjectURL(blob)
                          const a = document.createElement('a')
                          a.href = url
                          a.download = mem.file_name || 'memorial.pdf'
                          document.body.appendChild(a)
                          a.click()
                          window.URL.revokeObjectURL(url)
                          document.body.removeChild(a)
                        } catch (e: any) {
                          alert(e?.data?.detail || e?.message || 'Tải memorial thất bại')
                        }
                      }}
                    >
                      Tải memorial
                    </Button>
                  </Paper>
                )
              })()}

              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{ overflow: 'auto', maxHeight: { xs: 'none', md: 'calc(95vh - 140px)' } }}
              >

              {/* Rubric Selection */}
              {rubrics.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel id="memorial-rubric-label">Rubric chấm điểm</InputLabel>
                  <Select
                    labelId="memorial-rubric-label"
                    value={scoringForm.rubric_id}
                    label="Rubric chấm điểm"
                    onChange={(e) => {
                      const rubricId = e.target.value
                      setScoringForm({
                        ...scoringForm,
                        rubric_id: rubricId,
                        // Reset scores when changing rubric
                        rubric_scores: {},
                      })
                    }}
                    sx={{
                      textTransform: 'none',
                      '& .MuiSelect-select': {
                        py: 1.5,
                      },
                    }}
                  >
                    {rubrics
                      .filter((r) => !r.case_id || r.case_id === scoringForm.case_id)
                      .map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.name}
                          {r.case_id && r.case_id === scoringForm.case_id && (
                            <Chip label="Cuộc thi này" size="small" sx={{ ml: 1 }} />
                          )}
                          {!r.case_id && (
                            <Chip label="Chung" size="small" color="primary" sx={{ ml: 1 }} />
                          )}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}

              {/* AI Assistive Button */}
              {(scoringForm.memorial_id || scoringForm.transcript_id) && (
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<AutoAwesome />}
                    onClick={handleGetAISuggestions}
                    disabled={isLoadingAI || scoreFormLocked}
                    fullWidth
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      py: 1.5,
                    }}
                  >
                    {isLoadingAI ? <CircularProgress size={20} /> : 'AI Assistive'}
                  </Button>
                  {aiSuggestions && (
                    <Alert
                      severity={
                        (aiSuggestions.confidence ?? 0) >= 0.7
                          ? 'success'
                          : (aiSuggestions.confidence ?? 0) >= 0.4
                          ? 'info'
                          : 'warning'
                      }
                      sx={{ mt: 2 }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          🤖 Phân tích AI sơ bộ
                        </Typography>
                        {aiSuggestions.suggested_scores &&
                          Object.keys(aiSuggestions.suggested_scores).length > 0 && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={applyAllAISuggestions}
                              disabled={scoreFormLocked}
                              sx={{ textTransform: 'none' }}
                            >
                              Áp dụng tất cả
                            </Button>
                          )}
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                        {aiSuggestions.analysis}
                      </Typography>
                      {aiSuggestions.recommendations && aiSuggestions.recommendations.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                            Đề xuất:
                          </Typography>
                          <Box component="ul" sx={{ margin: 0, paddingLeft: 3, mt: 1 }}>
                            {aiSuggestions.recommendations.map((rec: string, idx: number) => (
                              <li key={idx}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>{rec}</Typography>
                              </li>
                            ))}
                          </Box>
                        </Box>
                      )}
                      {aiSuggestions.confidence != null && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Độ tin cậy:
                          </Typography>
                          <Box
                            sx={{
                              flex: 1,
                              height: 6,
                              borderRadius: 1,
                              background: '#E0E0E0',
                              overflow: 'hidden',
                              maxWidth: 180,
                            }}
                          >
                            <Box
                              sx={{
                                width: `${(aiSuggestions.confidence * 100).toFixed(0)}%`,
                                height: '100%',
                                background:
                                  aiSuggestions.confidence >= 0.7
                                    ? '#2E7D32'
                                    : aiSuggestions.confidence >= 0.4
                                    ? '#1565C0'
                                    : '#EF6C00',
                              }}
                            />
                          </Box>
                          <Typography variant="caption" fontWeight={600}>
                            {(aiSuggestions.confidence * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                        AI chỉ gợi ý — Giám khảo có toàn quyền quyết định điểm cuối (P5-AI-2).
                      </Typography>
                    </Alert>
                  )}
                  {scoringForm.memorial_id && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="text"
                        size="small"
                        disabled={prelimLoading || scoreFormLocked || !scoringForm.memorial_id}
                        onClick={async () => {
                          try {
                            const r = await runAiPreliminary(scoringForm.memorial_id).unwrap()
                            setAiPreliminary(r)
                          } catch (e: any) {
                            alert(e?.data?.detail || e?.message || 'RAG sơ bộ thất bại')
                          }
                        }}
                      >
                        {prelimLoading ? <CircularProgress size={18} /> : 'Chạy RAG sơ bộ (tham khảo, không ghi điểm)'}
                      </Button>
                      {aiPreliminary && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                            {aiPreliminary.governance_notice}
                          </Typography>
                          <Typography variant="body2">
                            Số đoạn trích: {aiPreliminary.excerpts?.length ?? 0}
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Rubric Scores */}
              <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                  Điểm theo Rubric
                </Typography>
                {rubricCriteria.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {rubricCriteria.map((criterion) => {
                      const aiVal = aiSuggestions?.suggested_scores?.[criterion.key]
                      const judgeVal = scoringForm.rubric_scores[criterion.key]
                      const diverged =
                        aiVal !== undefined &&
                        judgeVal !== undefined &&
                        Math.abs(judgeVal - aiVal) >= 1.0
                      return (
                      <Box
                        key={criterion.key}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: 2,
                          p: 1.5,
                          backgroundColor: 'grey.50',
                          borderRadius: 1,
                          flexDirection: { xs: 'column', sm: 'row' }
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body1" fontWeight={500} gutterBottom={!!criterion.description}>
                            {criterion.name}
                          </Typography>
                          {criterion.description && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                              {criterion.description}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                            {criterion.weight && criterion.weight !== 1.0 && (
                              <Chip label={`Weight: ${criterion.weight}`} size="small" />
                            )}
                            {aiVal !== undefined && (
                              <Chip
                                label={`🤖 AI: ${aiVal.toFixed(1)}`}
                                size="small"
                                color="info"
                                variant="outlined"
                                onClick={
                                  scoreFormLocked
                                    ? undefined
                                    : () => applyAISuggestionForCriterion(criterion.key)
                                }
                                sx={{ cursor: scoreFormLocked ? 'default' : 'pointer' }}
                              />
                            )}
                            {diverged && (
                              <Chip
                                label={`Lệch ${(judgeVal - aiVal).toFixed(1)}`}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          minWidth: { xs: '100%', sm: 140 },
                          justifyContent: { xs: 'flex-end', sm: 'flex-start' }
                        }}>
                          <TextField
                            type="number"
                            value={scoringForm.rubric_scores[criterion.key] || ''}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0
                              const clampedValue = Math.max(0, Math.min(value, criterion.max_score))
                              setScoringForm({
                                ...scoringForm,
                                rubric_scores: {
                                  ...scoringForm.rubric_scores,
                                  [criterion.key]: clampedValue,
                                },
                              })
                            }}
                            disabled={scoreFormLocked}
                            inputProps={{
                              min: 0,
                              max: criterion.max_score,
                              step: 0.1,
                              style: { textAlign: 'right', padding: '8px' }
                            }}
                            size="small"
                            sx={{
                              width: 90,
                              '& .MuiOutlinedInput-input': {
                                textAlign: 'right'
                              }
                            }}
                            placeholder="0"
                          />
                          <Typography variant="body1" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
                            / {criterion.max_score}
                          </Typography>
                        </Box>
                      </Box>
                    )})}
                  </Box>
                ) : (
                  <Alert severity="warning">
                    Chưa có rubric nào. Vui lòng chọn rubric hoặc tạo rubric mới.
                  </Alert>
                )}
              </Paper>

              {/* Total Score */}
              <Paper sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'primary.main' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Tổng điểm: {calculateTotalScore().toFixed(2)} / {getMaxScore().toFixed(1)}
                </Typography>
                {editingScore && (
                  <Typography variant="caption" color="text.secondary">
                    Điểm cũ: {editingScore.total_score.toFixed(2)} / 10
                  </Typography>
                )}
              </Paper>

              {/* Comments */}
              <TextField
                fullWidth
                label="Nhận xét"
                value={scoringForm.comments}
                onChange={(e) => setScoringForm({ ...scoringForm, comments: e.target.value })}
                multiline
                rows={4}
                disabled={scoreFormLocked}
                placeholder="Nhập nhận xét về memorial..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              </Grid>
            </Grid>
          )}

        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button 
            onClick={handleCloseScoringDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
            }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSubmitScore}
            variant="outlined"
            disabled={isCreating || isUpdating || isSubmitting || scoreFormLocked}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
              minWidth: 120,
            }}
          >
            {(isCreating || isUpdating) ? (
              <CircularProgress size={20} />
            ) : editingScore ? (
              'Lưu nháp'
            ) : (
              'Lưu nháp'
            )}
          </Button>
          <Button
            onClick={handleFinalizeScore}
            variant="contained"
            disabled={isCreating || isUpdating || isSubmitting || scoreFormLocked}
            color="success"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
              minWidth: 160,
            }}
          >
            {isSubmitting ? <CircularProgress size={20} /> : 'Hoàn tất & Gửi điểm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Rubric Dialog */}
      <Dialog open={rubricDialogOpen} onClose={() => setRubricDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Tạo Rubric mới</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Tên rubric *"
              value={newRubric.name}
              onChange={(e) => setNewRubric({ ...newRubric, name: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Cuộc thi (tùy chọn)</InputLabel>
              <Select
                value={newRubric.case_id || ''}
                label="Cuộc thi (tùy chọn)"
                onChange={(e) => setNewRubric({ ...newRubric, case_id: e.target.value || undefined })}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>Tất cả cuộc thi (Rubric chung)</span>
                    <Chip label="Chung" size="small" color="primary" />
                  </Box>
                </MenuItem>
                {cases.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {formatCaseDisplay(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Rubric chung:</strong> Có thể sử dụng cho tất cả các cuộc thi
                <br />
                <strong>Rubric riêng:</strong> Chỉ áp dụng cho cuộc thi được chọn
              </Typography>
            </Alert>
            <Divider />
            <Typography variant="h6">Tiêu chí chấm điểm</Typography>
            {newRubric.criteria.map((criterion, index) => (
              <Paper key={index} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Tên tiêu chí *"
                    value={criterion.name}
                    onChange={(e) => {
                      const updated = [...newRubric.criteria]
                      updated[index] = { ...criterion, name: e.target.value }
                      setNewRubric({ ...newRubric, criteria: updated })
                    }}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Mô tả"
                    value={criterion.description || ''}
                    onChange={(e) => {
                      const updated = [...newRubric.criteria]
                      updated[index] = { ...criterion, description: e.target.value }
                      setNewRubric({ ...newRubric, criteria: updated })
                    }}
                    fullWidth
                    multiline
                    rows={2}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Điểm tối đa *"
                      type="number"
                      value={criterion.max_score}
                      onChange={(e) => {
                        const updated = [...newRubric.criteria]
                        updated[index] = { ...criterion, max_score: parseFloat(e.target.value) || 0 }
                        setNewRubric({ ...newRubric, criteria: updated })
                      }}
                      inputProps={{ min: 0, step: 0.5 }}
                      required
                    />
                    <TextField
                      label="Trọng số (weight)"
                      type="number"
                      value={criterion.weight || 1}
                      onChange={(e) => {
                        const updated = [...newRubric.criteria]
                        updated[index] = { ...criterion, weight: parseFloat(e.target.value) || 1 }
                        setNewRubric({ ...newRubric, criteria: updated })
                      }}
                      inputProps={{ min: 0, step: 0.1 }}
                    />
                    <IconButton
                      color="error"
                      onClick={() => {
                        const updated = newRubric.criteria.filter((_, i) => i !== index)
                        setNewRubric({ ...newRubric, criteria: updated })
                      }}
                      disabled={newRubric.criteria.length === 1}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => {
                const newCriterion: RubricCriterion = {
                  key: `criterion_${newRubric.criteria.length + 1}`,
                  name: `Tiêu chí ${newRubric.criteria.length + 1}`,
                  max_score: 10,
                  weight: 1,
                }
                setNewRubric({
                  ...newRubric,
                  criteria: [...newRubric.criteria, newCriterion],
                })
              }}
            >
              Thêm tiêu chí
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRubricDialogOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!newRubric.name || newRubric.criteria.length === 0) {
                setSnackbar({
                  open: true,
                  message: 'Vui lòng điền đầy đủ thông tin',
                  severity: 'error',
                })
                return
              }
              try {
                // Convert criteria to match backend format
                const rubricData: RubricCreate = {
                  name: newRubric.name,
                  case_id: newRubric.case_id || undefined,
                  criteria: newRubric.criteria.map((c) => ({
                    key: c.key,
                    name: c.name,
                    description: c.description,
                    max_score: c.max_score,
                    weight: c.weight,
                  })),
                }
                const created = await createRubric(rubricData).unwrap()
                setSnackbar({
                  open: true,
                  message: 'Tạo rubric thành công',
                  severity: 'success',
                })
                setScoringForm({ ...scoringForm, rubric_id: created.id || '' })
                setRubricDialogOpen(false)
                setNewRubric({
                  name: '',
                  case_id: '',
                  criteria: [{ key: 'criterion_1', name: 'Tiêu chí 1', max_score: 10, weight: 1 }],
                })
              } catch (error: any) {
                setSnackbar({
                  open: true,
                  message: error?.data?.detail || 'Lỗi khi tạo rubric',
                  severity: 'error',
                })
              }
            }}
            disabled={isCreatingRubric || !newRubric.name || newRubric.criteria.length === 0}
          >
            {isCreatingRubric ? 'Đang tạo...' : 'Tạo rubric'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setSnackbar({ ...snackbar, open: false })}
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
